// The running summary block in a wizard's context rail.
//
// Extracted from WizardShell when the shell gained a second caller: setup fills
// in hostname/TLS/workers, the add-worker flow fills in where the worker runs
// and what it will be called. The shell takes the whole block as a ReactNode so
// it stays ignorant of either shape, and this keeps the two looking alike —
// same small-caps heading, same right-aligned values, same em dash for a value
// the operator has not chosen yet.

import type { ReactNode } from "react";

export interface WizardSummaryRow {
	label: string;
	value: ReactNode;
}

export function WizardSummaryList({
	heading,
	rows,
	children,
}: {
	heading: string;
	rows: WizardSummaryRow[];
	/** Optional note below the rows, e.g. where the console moves at launch. */
	children?: ReactNode;
}) {
	return (
		<div>
			<h2 className="mb-2 text-[0.7rem] font-medium uppercase tracking-[0.14em] text-muted-foreground">
				{heading}
			</h2>
			<dl className="flex flex-col gap-2">
				{rows.map((row) => (
					<div
						key={row.label}
						className="flex items-center justify-between gap-3 text-xs"
					>
						<dt className="text-muted-foreground">{row.label}</dt>
						<dd className="flex min-w-0 items-center justify-end">
							{row.value}
						</dd>
					</div>
				))}
			</dl>
			{children}
		</div>
	);
}

/** A value that is not decided yet. */
export function WizardSummaryPending() {
	return <span className="text-muted-foreground/60">—</span>;
}

/** A value the operator typed or picked, truncated rather than wrapped. */
export function WizardSummaryValue({ children }: { children: ReactNode }) {
	return (
		<span className="truncate font-mono text-[0.7rem] text-foreground">
			{children}
		</span>
	);
}
